import React, { useRef, useEffect, useState } from 'react';
import useInView from '../../hooks/useInView';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';

const AdvisorCTA = ({ onApply }) => {
  const [sectionRef, inView] = useInView(0.1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const canvasRef = useRef(null);
  const { settings: platformSettings } = usePlatformSettings();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const ctx = canvas.getContext('2d');
    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.5 + 0.4,
      speed: Math.random() * 0.35 + 0.1,
      opacity: Math.random() * 0.2 + 0.08,
    }));
    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.opacity})`;
        ctx.fill();
        p.y -= p.speed;
        if (p.y < -4) { p.y = canvas.height + 4; p.x = Math.random() * canvas.width; }
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  const handleApply = () => {
    if (onApply) {
      onApply();
    } else {
      window.open(platformSettings.advisor_application_url || 'https://form.typeform.com/to/IuLOXHct', '_blank');
    }
  };

  return (
    <section id="for-advisors" ref={sectionRef} style={{
      background: '#004D35',
      padding: isMobile ? '72px 24px 80px' : '100px 40px 120px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <canvas ref={canvasRef} style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 0,
      }} />

      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 140,
        background: 'linear-gradient(to bottom, transparent, #004D35)',
        zIndex: 1, pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 680, margin: '0 auto', position: 'relative', zIndex: 2, textAlign: 'center' }}>
        <div style={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <p style={{
            fontSize: 11, fontFamily: 'sans-serif', letterSpacing: '0.2em',
            color: '#a8f0c6', fontWeight: 700, textTransform: 'uppercase', marginBottom: 20,
          }}>
            ◆ FOR ADVISORS
          </p>

          <h2 style={{
            fontSize: isMobile ? 'clamp(38px, 10vw, 56px)' : 'clamp(44px, 6vw, 72px)',
            fontWeight: 400,
            fontFamily: 'Georgia, serif', color: '#fff', lineHeight: 1.05, marginBottom: 24,
          }}>
            Become a{' '}
            <em style={{ color: '#a8f0c6' }}>Verified Advisor.</em>
          </h2>

          <p style={{
            fontSize: isMobile ? 16 : 18,
            color: 'rgba(255,255,255,0.65)',
            maxWidth: 540, margin: '0 auto 36px',
            fontFamily: 'sans-serif', lineHeight: 1.7,
          }}>
            Business owners arrive with a real problem and book straight onto your calendar. No cold outreach. No lead fees. Never a cut of your work — one flat subscription.
          </p>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 10 : 36,
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 44,
          }}>
            {[
              '✓ Qualified owners, not tire-kickers',
              '✓ Bookings land on your calendar',
              '✓ Founding advisors get homepage placement',
            ].map(point => (
              <span key={point} style={{
                fontSize: 14, fontFamily: 'sans-serif',
                color: 'rgba(255,255,255,0.75)',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
              }}>
                {point}
              </span>
            ))}
          </div>

          <button
            onClick={handleApply}
            style={{
              background: '#ffffff', color: '#004D35',
              padding: '15px 40px', borderRadius: 9999,
              border: 'none', fontSize: 15, fontWeight: 700,
              fontFamily: 'sans-serif', cursor: 'pointer',
              transition: 'background 0.2s ease', display: 'inline-block',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.9)'}
            onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
          >
            Apply to Join Viysr →
          </button>

          <p style={{
            fontSize: 12, color: 'rgba(255,255,255,0.38)',
            fontFamily: 'sans-serif', marginTop: 16,
          }}>
            5-minute application · every advisor reviewed by hand · decision within 48 hours
          </p>
        </div>
      </div>
    </section>
  );
};

export default AdvisorCTA;
